import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { setE2eeUnlockHandler, type E2eeUnlockOptions, type E2eeUnlockResult } from '../utils/e2eeUnlockUi';
import { canUsePlatformPasskeys } from '../utils/webauthnPrf';

type Pending = {
  showRememberOptions?: boolean;
  title?: string;
  message?: string;
  resolve: (r: E2eeUnlockResult) => void;
  reject: (e: Error) => void;
};

const EyeIcon = ({ off }: { off: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
    {off ? <path d="M3 3l18 18" /> : null}
  </svg>
);

export default function E2eeUnlockModalHost() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [open, setOpen] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [remember, setRemember] = useState(true);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [usePasskey, setUsePasskey] = useState(false);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const passRef = useRef<HTMLInputElement | null>(null);

  const canSubmit = useMemo(() => !busy, [busy]);

  useEffect(() => {
    canUsePlatformPasskeys().then(setPasskeyAvailable).catch(() => setPasskeyAvailable(false));
    setE2eeUnlockHandler(async (opts?: E2eeUnlockOptions) => {
      return await new Promise<E2eeUnlockResult>((resolve, reject) => {
        setPassphrase('');
        setRemember(true);
        setUsePasskey(false);
        setShow(false);
        setBusy(false);
        setPending({ ...opts, resolve, reject, showRememberOptions: opts?.showRememberOptions ?? true });
        setOpen(true);
        window.setTimeout(() => passRef.current?.focus(), 0);
      });
    });
    return () => setE2eeUnlockHandler(null);
  }, []);

  const cancel = () => {
    const p = pending;
    setOpen(false);
    setPending(null);
    setBusy(false);
    if (p) p.reject(new Error('Missing passphrase'));
  };

  const submit = async () => {
    if (!pending) return;
    if (!canSubmit) return;
    setBusy(true);
    try {
      const showRememberOptions = pending.showRememberOptions ?? true;
      const rememberOut = showRememberOptions ? remember : false;
      const usePasskeyOut = showRememberOptions && remember && passkeyAvailable ? usePasskey : false;
      pending.resolve({ passphrase: passphrase.trim(), remember: rememberOut, usePasskey: usePasskeyOut });
      setOpen(false);
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={cancel}
      title={pending?.title || 'Deblochează criptarea'}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
      showCloseButton={!busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={cancel} disabled={busy}>
            Anulează
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Se deblochează…' : 'Deblochează'}
          </button>
        </>
      }
    >
      <div style={{ padding: 'var(--sp-5)' }}>
        <p className="muted" style={{ marginTop: 0 }}>
          {pending?.message ||
            'Introdu passphrase-ul tău E2EE ca să poți decripta documente. Poți alege să ții minte cheia pe acest device.'}
        </p>

        <div className="form-group">
          <label className="form-label">Passphrase</label>
          <div style={{ position: 'relative' }}>
            <input
              ref={passRef}
              className="form-input"
              type={show ? 'text' : 'password'}
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              disabled={busy}
              placeholder="Minim 8 caractere"
              style={{ paddingRight: 44 }}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShow((v) => !v)}
              disabled={busy}
              aria-label={show ? 'Ascunde passphrase' : 'Afișează passphrase'}
              title={show ? 'Ascunde' : 'Afișează'}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 34,
                height: 30,
                padding: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <EyeIcon off={!show} />
            </button>
          </div>
        </div>

        {(pending?.showRememberOptions ?? true) && (
          <>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={busy}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 'var(--text-sm)' }}>
                Ține minte pe acest device (nu vei mai introduce passphrase-ul). Dacă cineva are acces la acest browser/profil,
                poate decripta.
              </span>
            </label>

            {passkeyAvailable && remember && (
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={usePasskey}
                  onChange={(e) => setUsePasskey(e.target.checked)}
                  disabled={busy}
                  style={{ marginTop: 3 }}
                />
                <span style={{ fontSize: 'var(--text-sm)' }}>
                  Cere FaceID/TouchID/Windows Hello pentru a debloca cheia salvată (dacă este suportat de browser/dispozitiv).
                </span>
              </label>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

